import type { TicketHistoryResponse } from '../../types/member.js';
import { useTranslation } from 'react-i18next';

/** @graph-connects none */
const TICKET_STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  有効: { bg: 'var(--color-success-bg)', text: 'var(--color-success)' },
  使用済み: { bg: 'var(--color-bg-elevated)', text: 'var(--color-text-muted)' },
  期限切れ: { bg: 'var(--color-error-bg)', text: 'var(--color-error)' },
};

interface TicketTabProps {
  data: TicketHistoryResponse;
}

/** @graph-connects none */
export function TicketTab({ data }: TicketTabProps) {
  const { t } = useTranslation();

  if (data.tickets.length === 0) {
    return (
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] py-12 text-center text-sm text-[var(--color-text-muted)]">
        {t('member.noTicketHistory')}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="border-b border-[var(--color-border)] px-4 py-3 text-[13px] font-semibold text-[var(--color-text-primary)]">
        {t('member.ticketHistory')}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('member.claimedDate')}
              </th>
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('member.ticketType')}
              </th>
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('member.expirationDate')}
              </th>
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('common.status')}
              </th>
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {t('member.usedOrder')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.tickets.map((ticket, i) => {
              const st = TICKET_STATUS_STYLES[ticket.status];
              return (
                <tr key={i} className="border-b border-[var(--color-border)]">
                  <td className="whitespace-nowrap px-3.5 py-2.5 font-mono text-[12px]">
                    {formatDate(ticket.createdAt)}
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-2.5 text-[13px]">
                    {ticket.ticketType}
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-2.5 font-mono text-[12px]">
                    {ticket.expiredAt ? formatDate(ticket.expiredAt) : '-'}
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-2.5">
                    <span
                      className="inline-block rounded-full px-2 py-0.5 text-[11px] font-medium"
                      style={{
                        background: st?.bg ?? 'var(--color-bg-elevated)',
                        color: st?.text ?? 'var(--color-text-muted)',
                      }}
                    >
                      {ticket.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3.5 py-2.5 font-mono text-[12px]">
                    {ticket.usedOrderId ?? '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** @graph-connects none */
function formatDate(isoDate: string): string {
  const _d = new Date(isoDate);
  if (Number.isNaN(_d.getTime())) return isoDate;
  return _d.toLocaleDateString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}
